import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * UI state of the debounced price-preview call (#1203) while no breakdown is
 * rendered:
 * - 'idle'    — the form inputs don't determine a price yet (no request sent)
 * - 'loading' — the first preview for the current inputs is in flight
 * - 'error'   — the preview failed and no breakdown is available
 */
export type PricePreviewStatus = 'idle' | 'loading' | 'error';

/**
 * How the box fills the idle state — exactly one of the two, chosen by the
 * caller because the price boxes differ (the shared-skeleton variant would be
 * wrong for the other one):
 *
 * - `rowLabelKeys` renders the rows that WILL appear, each with "wird
 *   berechnet" instead of a value, so an operator filling in the form sees the
 *   shape of the answer (CEO, 2026-08-17).
 * - `idleHintKey` renders one line saying WHY no price can be shown yet, for
 *   the boxes that have no row skeleton. It replaces a bare "n.z.", which reads
 *   as "not applicable" — i.e. as if the booking were free of charge — where
 *   the truth is that the price is simply not computable until an object is
 *   picked ("A Failure Must Never Look Like an Absence"; same move as #1591 on
 *   `SelectField`'s "No options available").
 */
type IdleContent =
    | { rowLabelKeys: string[], idleHintKey?: undefined }
    | { idleHintKey: string, rowLabelKeys?: undefined };

type Props = Readonly<{ status: PricePreviewStatus } & IdleContent>;

/**
 * Shown in place of the money rows of a price box while no BE breakdown is
 * available. There is deliberately no FE-computed fallback number behind any of
 * these states.
 *
 * A zero is NOT used as the placeholder. "0,00 €" cannot be told apart from a
 * genuinely free booking — the same ambiguity #2036 removed from the pricing
 * engine, and it would be worse here, in front of an operator about to save.
 */
function PricePreviewStateRow(props: Props) {
    const { t } = useTranslation();
    // `status` destructured, the idle content read off `props`: narrowing the
    // exclusive union below needs the object, not the loose properties.
    const { status } = props;

    if (status === 'error') {
        return (
            <div className={'flex justify-between text-sm text-red-500'}>
                <span>{t('booking_details.reservation_summary.price_preview_error')}</span>
            </div>
        );
    }

    if (status === 'loading') {
        return (
            <div className={'flex justify-between text-sm text-gray-500 animate-pulse'}>
                <span>{t('common.loading')}</span>
            </div>
        );
    }

    if (props.rowLabelKeys !== undefined) {
        return (
            <>
                {props.rowLabelKeys.map((key) => (
                    <div key={key} className={'text-sm mb-2 flex justify-between text-foreground/40'}>
                        <strong>{t(key)}</strong>
                        <span>{t('booking_details.reservation_summary.pending_calculation')}</span>
                    </div>
                ))}
            </>
        );
    }

    return (
        <div className={'flex justify-between text-sm text-foreground/60'}>
            <span>{t(props.idleHintKey)}</span>
        </div>
    );
}

export default PricePreviewStateRow;
